import React, { useState } from 'react';
import { CSVUpload } from '../components/CSVUpload/CSVUpload';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { CheckCircle, AlertCircle, Info } from 'lucide-react';

export default function DataUpload() {
  const [uploadResult, setUploadResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  const handleUploadComplete = (result: any) => {
    setUploadResult(result);
    setError(null);
  };

  const handleError = (errorMessage: string) => {
    setError(errorMessage);
    setUploadResult(null);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto p-6">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Data Upload</h1>
          <p className="text-gray-600">
            Upload CSV files to create dynamic tables in the backend. 
            The system will automatically detect the schema and create the appropriate table structure.
          </p>
        </div>

        {/* Info Card */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Info className="w-5 h-5" />
              How It Works
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 text-sm text-gray-600">
              <p>1. <strong>Upload CSV:</strong> Select or drag & drop your CSV file</p>
              <p>2. <strong>Preview:</strong> Review the detected columns and sample data</p>
              <p>3. <strong>Auto-Schema:</strong> Backend creates a table based on your CSV structure</p>
              <p>4. <strong>Data Storage:</strong> Your data is stored and ready for analysis</p>
            </div>
          </CardContent>
        </Card>

        {/* Error Alert */}
        {error && (
          <Alert className="mb-6" variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Success Alert */}
        {uploadResult && (
          <Alert className="mb-6">
            <CheckCircle className="h-4 w-4" />
            <AlertDescription>
              <div className="space-y-2">
                <p className="font-medium">CSV uploaded successfully!</p>
                <div className="text-sm space-y-1">
                  <p><strong>Table Name:</strong> {uploadResult.tableName}</p>
                  <p><strong>Columns:</strong> {uploadResult.columns?.length || 0}</p>
                  <p><strong>Rows:</strong> {uploadResult.rowCount || 0}</p>
                  {uploadResult.sampleData && (
                    <p><strong>Sample Data:</strong> Available in backend</p>
                  )}
                </div>
              </div>
            </AlertDescription>
          </Alert>
        )}

        {/* CSV Upload Component */}
        <CSVUpload
          onUploadComplete={handleUploadComplete}
          onError={handleError}
        />

        {/* Upload Result Details */}
        {uploadResult && (
          <Card className="mt-6">
            <CardHeader>
              <CardTitle>Upload Details</CardTitle>
              <CardDescription>
                Details about the created table and uploaded data
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <h4 className="font-medium mb-2">Table Schema:</h4>
                  <div className="bg-gray-50 p-4 rounded">
                    <pre className="text-sm overflow-x-auto">
                      {JSON.stringify(uploadResult, null, 2)}
                    </pre>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

